// src/context/NetworkContext.js

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';
import {
  collection, query, orderBy, limit, onSnapshot, updateDoc, startAfter,
  getDocs, where, doc, getDoc
} from 'firebase/firestore';

// Create context
const NetworkContext = createContext();

// Create Provider
export const NetworkProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Listen for unread messages
  useEffect(() => {
    if (!currentUser) {
      setUnreadMessages(0);
      setLoading(false);
      return;
    }

    // Query conversations where current user is a participant
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.uid)
    );

    // Subscribe to conversations
    const unsubscribe = onSnapshot(
      conversationsQuery,
      (snapshot) => {
        let totalUnread = 0;

        snapshot.forEach(doc => {
          const conversation = doc.data();
          totalUnread += conversation.unreadCount?.[currentUser.uid] || 0;
        });

        setUnreadMessages(totalUnread);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching unread messages:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Listen for pending connection requests
  useEffect(() => {
    if (!currentUser) {
      setPendingConnectionCount(0);
      return;
    }

    const pendingQuery = query(
      collection(db, 'connections'),
      where('receiverId', '==', currentUser.uid),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
      pendingQuery,
      (snapshot) => {
        setPendingConnectionCount(snapshot.size);
      },
      (error) => {
        console.error('Error fetching pending connections:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Load feed posts with pagination and error handling
  const loadFeed = useCallback(async (startAfterDoc = null, postsPerPage = 10) => {
    try {
      let feedQuery;

      // Base query - ordered by creation date
      if (startAfterDoc) {
        feedQuery = query(
          collection(db, 'posts'),
          orderBy('createdAt', 'desc'),
          startAfter(startAfterDoc),
          limit(postsPerPage)
        );
      } else {
        feedQuery = query(
          collection(db, 'posts'),
          orderBy('createdAt', 'desc'),
          limit(postsPerPage)
        );
      }

      // Try to get posts, with fallback
      let postsSnapshot;
      try {
        postsSnapshot = await getDocs(feedQuery);
      } catch (error) {
        console.error('Error loading feed:', error);
        // Return mock data as fallback
        return {
          posts: getMockPosts(),
          lastVisible: null
        };
      }

      const lastVisible = postsSnapshot.docs[postsSnapshot.docs.length - 1];

      // Process posts data
      const postsPromises = postsSnapshot.docs.map(async (postDoc) => {
        const postData = postDoc.data();
        const authorBusinessId = postData.businessId || postData.authorId;

        // Default author data
        let authorData = {
          id: authorBusinessId || 'unknown',
          name: postData.authorName || 'Business'
        };

        // Try to get author data
        try {
          if (authorBusinessId) {
            const authorRef = doc(db, 'businesses', authorBusinessId);
            const authorSnap = await getDoc(authorRef);

            if (authorSnap.exists()) {
              authorData = {
                id: authorSnap.id,
                ...authorSnap.data(),
                name: authorSnap.data()?.name || postData.authorName || 'Business'
              };
            }
          }
        } catch (err) {
          console.error('Error fetching post author:', err);
        }

        // Check if current user has liked/saved this post
        let userHasLiked = false;
        let userHasSaved = false;

        if (currentUser) {
          try {
            const likeRef = doc(db, 'postLikes', `${currentUser.uid}_${postDoc.id}`);
            const likeSnap = await getDoc(likeRef);
            userHasLiked = likeSnap.exists();

            const savedRef = doc(db, 'savedPosts', `${currentUser.uid}_${postDoc.id}`);
            const savedSnap = await getDoc(savedRef);
            userHasSaved = savedSnap.exists();
          } catch (err) {
            console.error('Error checking post interactions:', err);
          }
        }

        return {
          id: postDoc.id,
          ...postData,
          createdAt: postData.createdAt?.toDate() || new Date(),
          author: authorData,
          userHasLiked,
          userHasSaved
        };
      });

      const posts = await Promise.all(postsPromises);

      return { posts, lastVisible };
    } catch (error) {
      console.error('Error loading feed:', error);
      return { posts: getMockPosts(), lastVisible: null };
    }
  }, [currentUser]);

  // Mock posts for fallback
  const getMockPosts = () => {
    return [
      {
        id: 'mock1',
        content: 'We just launched our new product line! Check out our website for details.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
        author: { id: 'mock-author-1', name: 'Tech Innovations', industry: 'Technology' },
        likeCount: 24,
        commentCount: 7,
        userHasLiked: false,
        userHasSaved: false
      },
      {
        id: 'mock2',
        content: 'Looking for partners in the sustainable energy sector. DM us if interested in collaboration!',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
        author: { id: 'mock-author-2', name: 'Green Energy Solutions', industry: 'Energy' },
        likeCount: 15,
        commentCount: 3,
        userHasLiked: false,
        userHasSaved: false
      },
      {
        id: 'mock3',
        content: 'Join our upcoming webinar on business growth strategies in challenging markets.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48), // 2 days ago
        author: { id: 'mock-author-3', name: 'Business Growth Experts', industry: 'Consulting' },
        tags: ['webinar', 'business', 'growth'],
        likeCount: 32,
        commentCount: 9,
        userHasLiked: false,
        userHasSaved: false
      }
    ];
  };

  // Mark a conversation as read
  const markConversationAsRead = async (conversationId) => {
    if (!currentUser || !conversationId) return;

    try {
      const conversationRef = doc(db, 'conversations', conversationId);

      // Create an update object to set this user's unread count to 0
      const updateData = {
        [`unreadCount.${currentUser.uid}`]: 0
      };

      await updateDoc(conversationRef, updateData);
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  };

  const value = {
    loadFeed,
    unreadMessages,
    pendingConnectionCount,
    markConversationAsRead,
    loading
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

// Create hook for using the context
export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
